import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Membership, Org, Profile, AppRole, ChannelSchedule, ContentSignals } from '@/types/database';
import { parseTargetAudience, parseToneOfVoice } from '@/types/database';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  membership: Membership | null;
  org: Org | null;
  isLoading: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [org, setOrg] = useState<Org | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = membership?.role === 'admin';
  const canEdit = membership?.role === 'admin' || membership?.role === 'editor';

  // Fetch user data (profile, membership, org)
  const fetchUserData = async (userId: string) => {
    try {
      // Fetch profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      setProfile(profileData as Profile | null);

      // Fetch membership (order by created_at DESC to get the most recent)
      const { data: membershipData } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      setMembership(membershipData as Membership | null);

      // Fetch org if membership exists
      if (membershipData?.org_id) {
        const { data: orgData } = await supabase
          .from('orgs')
          .select('*')
          .eq('id', membershipData.org_id)
          .single();
        
        if (orgData) {
          // Transform to match our Org type
          const transformedOrg: Org = {
            id: orgData.id,
            name: orgData.name,
            created_at: orgData.created_at,
            website_url: orgData.website_url,
            description: orgData.description,
            products_services: Array.isArray(orgData.products_services) 
              ? orgData.products_services as Array<{ name: string; description?: string }>
              : null,
            target_audience: parseTargetAudience(orgData.target_audience),
            tone_of_voice: parseToneOfVoice(orgData.tone_of_voice),
            industry_areas: orgData.industry_areas,
            competitors: orgData.competitors,
            scraped_at: orgData.scraped_at,
            unique_selling_points: (orgData as Record<string, unknown>).unique_selling_points as string[] | null,
            seasonality_description: (orgData as Record<string, unknown>).seasonality_description as string | null,
            geographic_focus: (orgData as Record<string, unknown>).geographic_focus as string | null,
            content_signals: (orgData as Record<string, unknown>).content_signals as ContentSignals | null,
            enabled_channels: orgData.enabled_channels,
            channel_schedule: (orgData.channel_schedule as ChannelSchedule) || {},
          };
          setOrg(transformedOrg);
        } else {
          setOrg(null);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Listener för PÅGÅENDE auth-ändringar (styr INTE isLoading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        
        // Fire and forget - vänta inte, ändra inte loading
        if (session?.user) {
          fetchUserData(session.user.id);
        } else {
          setProfile(null);
          setMembership(null);
          setOrg(null);
        }
      }
    );

    // INITIAL load (styr isLoading)
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        // Hämta user data INNAN vi sätter loading false
        if (session?.user) {
          await fetchUserData(session.user.id);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await fetchUserData(session.user.id);
      }
    }
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshAuth = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        membership,
        org,
        isLoading,
        isAdmin,
        canEdit,
        signIn,
        signUp,
        signOut,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
